import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExpert, ExpertProfile, isQualifiedExpert, ExpertSession } from '../../contexts/ExpertContext';
import { BrCard, BrButton, BrInput, BrSelect, BrMessage } from '@govbr-ds/react-components';

// Generate provisional recovery code (same algorithm as context)
function generateProvisionalCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoids 0/O, 1/I/L
    let code = '';
    for (let i = 0; i < 6; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
}

const PROVISIONAL_CODE_KEY = 'lumen_provisional_code';

export default function ExpertWelcomePage() {
    const navigate = useNavigate();
    const { createProvisionalSession, updateProfileField, completeProfile, recoverSession, checkDeviceSession, state } = useExpert();
    const [formData, setFormData] = useState<Partial<ExpertProfile>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { t } = useTranslation();
    const [sessionCreated, setSessionCreated] = useState(false);

    // Auto-recovery states
    const [deviceSession, setDeviceSession] = useState<ExpertSession | null>(null);
    const [isCheckingDevice, setIsCheckingDevice] = useState(true);
    const [autoRecoveryDismissed, setAutoRecoveryDismissed] = useState(false);

    // Provisional code - generated immediately
    const [provisionalCode] = useState<string>(() => {
        // Try to restore from localStorage first (persistence across refresh)
        const stored = localStorage.getItem(PROVISIONAL_CODE_KEY);
        if (stored) return stored;
        const newCode = generateProvisionalCode();
        localStorage.setItem(PROVISIONAL_CODE_KEY, newCode);
        return newCode;
    });
    const [codeCopied, setCodeCopied] = useState(false);

    // Manual recovery
    const [recoveryCode, setRecoveryCode] = useState('');
    const [recoveryError, setRecoveryError] = useState('');
    const [showManualRecovery, setShowManualRecovery] = useState(false);

    // Ref for scrolling to form errors
    const profileFormRef = useRef<HTMLDivElement>(null);

    const expertiseOptions = [
        { value: '', label: t('expert.welcomePage.selectOption') },
        { value: 'ux_ui_design', label: t('expert.options.expertise.uxUiDesign') },
        { value: 'product_management', label: t('expert.options.expertise.productManagement') },
        { value: 'frontend_dev', label: t('expert.options.expertise.frontendDev') },
        { value: 'backend_dev', label: t('expert.options.expertise.backendDev') },
        { value: 'qa_quality', label: t('expert.options.expertise.qaQuality') },
        { value: 'business_analysis', label: t('expert.options.expertise.businessAnalysis') },
        { value: 'information_architecture', label: t('expert.options.expertise.informationArchitecture') },
        { value: 'accessibility', label: t('expert.options.expertise.accessibility') },
        { value: 'other', label: t('expert.options.expertise.other') }
    ];

    const experienceOptions = [
        { value: '', label: t('expert.welcomePage.selectOption') },
        { value: '<1', label: t('expert.options.experience.lessThan1') },
        { value: '1-2', label: t('expert.options.experience.1to2') },
        { value: '2-5', label: t('expert.options.experience.2to5') },
        { value: '5-10', label: t('expert.options.experience.5to10') },
        { value: '>10', label: t('expert.options.experience.moreThan10') }
    ];

    const genderOptions = [
        { value: '', label: t('expert.welcomePage.selectOption') },
        { value: 'female', label: t('expert.options.gender.female') },
        { value: 'male', label: t('expert.options.gender.male') },
        { value: 'non_binary', label: t('expert.options.gender.nonBinary') },
        { value: 'prefer_not_say', label: t('expert.options.gender.preferNotSay') }
    ];

    const ageOptions = [
        { value: '', label: t('expert.welcomePage.selectOption') },
        { value: '18-25', label: t('expert.options.age.18to25') },
        { value: '26-35', label: t('expert.options.age.26to35') },
        { value: '36-45', label: t('expert.options.age.36to45') },
        { value: '46-55', label: t('expert.options.age.46to55') },
        { value: '56+', label: t('expert.options.age.56plus') }
    ];

    const accessibilityOptions = [
        { value: '', label: t('expert.welcomePage.selectOption') },
        { value: 'none', label: t('expert.options.accessibilityNeed.none') },
        { value: 'visual', label: t('expert.options.accessibilityNeed.visual') },
        { value: 'auditory', label: t('expert.options.accessibilityNeed.auditory') },
        { value: 'motor', label: t('expert.options.accessibilityNeed.motor') },
        { value: 'cognitive', label: t('expert.options.accessibilityNeed.cognitive') },
        { value: 'other', label: t('expert.options.accessibilityNeed.other') }
    ];

    const educationOptions = [
        { value: '', label: t('expert.welcomePage.selectOption') },
        { value: 'undergrad_incomplete', label: t('expert.options.education.undergradIncomplete') },
        { value: 'undergrad_complete', label: t('expert.options.education.undergradComplete') },
        { value: 'specialization', label: t('expert.options.education.specialization') },
        { value: 'masters', label: t('expert.options.education.masters') },
        { value: 'doctorate', label: t('expert.options.education.doctorate') }
    ];

    // Check for existing device session on mount
    useEffect(() => {
        const checkDevice = async () => {
            setIsCheckingDevice(true);
            try {
                const existingSession = await checkDeviceSession();
                if (existingSession && existingSession.status === 'in_progress') {
                    setDeviceSession(existingSession);
                }
            } catch (error) {
                console.warn('Failed to check device session:', error);
            } finally {
                setIsCheckingDevice(false);
            }
        };
        checkDevice();
    }, [checkDeviceSession]);

    // Create provisional session when code is generated, or recover existing session
    useEffect(() => {
        const createSession = async () => {
            if (provisionalCode && !sessionCreated && !state.session) {
                const success = await createProvisionalSession(provisionalCode);
                if (success) {
                    setSessionCreated(true);
                    console.log('Provisional session created/recovered with code:', provisionalCode);
                }
            }
        };
        createSession();
    }, [provisionalCode, sessionCreated, state.session, createProvisionalSession]);

    // Handle session recovery: pre-fill form with saved profile data or redirect if complete
    useEffect(() => {
        if (state.session) {
            // If session has a complete profile, redirect to continue evaluation
            if (state.session.profile_status === 'complete' && state.session.interfaces_to_evaluate.length > 0) {
                console.log('Session recovered with complete profile, redirecting...');
                // Clear provisional code since we have a complete session
                localStorage.removeItem(PROVISIONAL_CODE_KEY);
                navigate('/especialista/instrucoes');
                return;
            }

            // Pre-fill form with saved profile data (for incomplete profiles)
            const savedProfile = state.session.profile;
            if (savedProfile && Object.keys(savedProfile).length > 0) {
                console.log('Restoring saved profile data:', savedProfile);
                setFormData(prev => ({
                    ...prev,
                    ...savedProfile
                }));
            }
        }
    }, [state.session, navigate]);

    // Auto-save profile fields when they change
    const handleFieldChange = useCallback((field: keyof ExpertProfile, value: ExpertProfile[keyof ExpertProfile]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Update in context for auto-save to Firestore
        if (state.session) {
            updateProfileField(field, value);
        }
    }, [state.session, updateProfileField]);

    const copyToClipboard = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(provisionalCode);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = provisionalCode;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        }
    }, [provisionalCode]);

    const validate = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.expertise) newErrors.expertise = t('expert.welcomePage.validation.selectExpertise');
        if (formData.expertise === 'other' && !formData.other_expertise?.trim()) {
            newErrors.other_expertise = t('expert.welcomePage.validation.specifyExpertise');
        }
        if (!formData.years_experience) newErrors.years_experience = t('expert.welcomePage.validation.selectExperience');
        if (!formData.gender) newErrors.gender = t('expert.welcomePage.validation.selectGender');
        if (!formData.age_range) newErrors.age_range = t('expert.welcomePage.validation.selectAge');
        if (!formData.accessibility_need) newErrors.accessibility_need = t('expert.welcomePage.validation.selectAccessibility');
        if (formData.accessibility_need === 'other' && !formData.other_accessibility?.trim()) {
            newErrors.other_accessibility = t('expert.welcomePage.validation.specifyAccessibility');
        }
        if (!formData.education) newErrors.education = t('expert.welcomePage.validation.selectEducation');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) {
            // Scroll to form to show validation errors
            profileFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }

        const profile = {
            ...formData,
            is_qualified: isQualifiedExpert(formData as ExpertProfile)
        } as ExpertProfile;

        // Clear provisional code from localStorage once session starts
        localStorage.removeItem(PROVISIONAL_CODE_KEY);

        // Use completeProfile if we have a provisional session, otherwise use startSession
        const success = await completeProfile(profile);
        if (success) {
            window.scrollTo(0, 0);
            navigate('/especialista/instrucoes');
        }
    };

    const handleAutoRecover = async () => {
        if (!deviceSession) return;

        const success = await recoverSession(deviceSession.recovery_code);
        if (success) {
            // Only navigate if profile is complete, otherwise stay on page to complete profile
            // The useEffect on state.session will pre-fill the form with saved data
            if (state.session?.profile_status === 'complete') {
                navigate('/especialista/instrucoes');
            }
            // If profile incomplete, setAutoRecoveryDismissed to show the form
            setAutoRecoveryDismissed(true);
        }
    };

    const handleManualRecover = async () => {
        if (!recoveryCode || recoveryCode.length !== 6) {
            setRecoveryError(t('expert.welcomePage.codeError'));
            return;
        }

        setRecoveryError('');
        const success = await recoverSession(recoveryCode.toUpperCase());

        if (success) {
            // Only navigate if profile is complete, otherwise stay on page to complete profile
            // The useEffect on state.session will pre-fill the form with saved data
            if (state.session?.profile_status === 'complete') {
                navigate('/especialista/instrucoes');
            }
            // If profile incomplete, just close the manual recovery section
            // The form will be pre-filled by the useEffect
            setShowManualRecovery(false);
        } else {
            setRecoveryError(state.error || t('expert.welcomePage.codeNotFound'));
        }
    };

    const dismissAutoRecovery = () => {
        setAutoRecoveryDismissed(true);
        setDeviceSession(null);
    };

    // Show loading while checking device
    if (isCheckingDevice) {
        return (
            <div className="container-lg py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6 text-center">
                        <BrCard>
                            <div className="card-content py-5">
                                <i className="fas fa-spinner fa-spin text-4xl text-primary-default mb-4 d-block"></i>
                                <p className="text-lg text-gray-60 m-0">{t('expert.welcomePage.checkingSession')}</p>
                            </div>
                        </BrCard>
                    </div>
                </div>
            </div>
        );
    }

    // Show auto-recovery prompt if device has existing session
    if (deviceSession && !autoRecoveryDismissed) {
        const progress = Math.round((deviceSession.evaluations.length / deviceSession.interfaces_to_evaluate.length) * 100);

        return (
            <div className="container-lg py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        <BrCard>
                            <div className="card-content">
                                <div className="text-center mb-4">
                                    <div className="d-inline-flex align-items-center justify-content-center bg-success-alternative rounded-circle mb-3" style={{ width: '64px', height: '64px' }}>
                                        <i className="fas fa-user-check text-3xl text-pure-0"></i>
                                    </div>
                                    <h2 className="text-2xl text-weight-bold text-gray-80 mb-2">
                                        {t('expert.welcomePage.welcomeBack')}
                                    </h2>
                                    <p className="text-lg text-gray-60 mb-4">
                                        {t('expert.welcomePage.foundSession')}
                                    </p>
                                </div>

                                {/* Session info */}
                                <div className="bg-gray-2 rounded p-4 mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="text-base text-gray-60">{t('expert.welcomePage.sessionCode')}</span>
                                        <span className="text-lg text-weight-bold font-monospace">{deviceSession.recovery_code}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="text-base text-gray-60">{t('expert.welcomePage.progressLabel')}</span>
                                        <span className="text-lg text-weight-medium">
                                            {deviceSession.evaluations.length} {t('expert.welcomePage.interfacesOf')} {deviceSession.interfaces_to_evaluate.length} {t('expert.welcomePage.interfaces')}
                                        </span>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="bg-gray-10 rounded" style={{ height: '8px' }}>
                                        <div
                                            className="bg-success-default rounded"
                                            style={{ height: '8px', width: `${progress}%`, transition: 'width 0.3s' }}
                                        />
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="d-flex flex-column gap-3">
                                    <BrButton
                                        primary
                                        block
                                        onClick={handleAutoRecover}
                                        disabled={state.isLoading}
                                        size="large"
                                        aria-label={t('expert.welcomePage.continueEvaluation')}
                                    >
                                        <i className="fas fa-play me-2" aria-hidden="true"></i>
                                        {t('expert.welcomePage.continueEvaluation')}
                                    </BrButton>
                                    <BrButton
                                        secondary
                                        block
                                        onClick={dismissAutoRecovery}
                                        aria-label={t('expert.welcomePage.startNewEvaluation')}
                                    >
                                        {t('expert.welcomePage.startNewEvaluation')}
                                    </BrButton>
                                </div>
                            </div>
                        </BrCard>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-lg py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-md-10 col-lg-8 d-flex flex-column gap-4">

                    {/* Card 1: Recovery Code + Manual Recovery */}
                    <BrCard>
                        <div className="card-content">
                            {/* Prominent Recovery Code Display */}
                            <div className="text-center mb-4">
                                <div className="d-flex justify-content-center w-100">
                                    <i className="fas fa-key text-3xl text-primary-default mb-2"></i>
                                </div>
                                <h2 className="text-xl text-weight-semi-bold text-gray-80 m-0 mb-1">
                                    {t('expert.welcomePage.yourRecoveryCode')}
                                </h2>
                                <p className="text-base text-gray-50 m-0 mb-3">
                                    {t('expert.welcomePage.saveCodeHint')}
                                </p>

                                {/* Code Display */}
                                <div className="d-flex flex-column align-items-center gap-2 bg-gray-2 rounded-lg px-3 py-3 mb-2">
                                    <span
                                        className="text-2xl text-weight-bold font-monospace text-primary-default"
                                        style={{ letterSpacing: '0.15em' }}
                                    >
                                        {provisionalCode}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={copyToClipboard}
                                        className="br-button secondary small"
                                        title={t('expert.welcomePage.copyCode')}
                                    >
                                        <i className={`fas ${codeCopied ? 'fa-check text-success-default' : 'fa-copy'} me-2`}></i>
                                        {t('expert.welcomePage.copyCode')}
                                    </button>
                                </div>
                                {codeCopied && (
                                    <p className="text-base text-success-default m-0">{t('expert.welcomePage.copied')}</p>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="d-flex align-items-center gap-3 my-4">
                                <div className="flex-fill border-top border-gray-10"></div>
                                <span className="text-base text-gray-40">{t('expert.welcomePage.or')}</span>
                                <div className="flex-fill border-top border-gray-10"></div>
                            </div>

                            {/* Manual Recovery Section */}
                            <div className="d-flex justify-content-center">
                                <button
                                    type="button"
                                    className="br-button secondary small px-4"
                                    onClick={() => setShowManualRecovery(!showManualRecovery)}
                                >
                                    <i className="fas fa-sync"></i>  {showManualRecovery ? t('expert.welcomePage.close') : t('expert.welcomePage.haveCode')}
                                </button>
                            </div>

                            {showManualRecovery && (
                                <div className="mt-4 pt-4 border-top border-gray-10">
                                    <div className="d-flex flex-column flex-sm-row gap-3 align-items-start">
                                        <div className="flex-fill w-100">
                                            <BrInput
                                                id="recovery-code"
                                                label={t('expert.welcomePage.enterRecoveryCode')}
                                                placeholder={t('expert.welcomePage.recoveryPlaceholder')}
                                                value={recoveryCode}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setRecoveryCode(e.target.value.toUpperCase().slice(0, 6));
                                                    setRecoveryError('');
                                                }}
                                                maxLength={6}
                                            />
                                        </div>
                                        <BrButton
                                            primary
                                            onClick={handleManualRecover}
                                            disabled={recoveryCode.length !== 6 || state.isLoading}
                                            className="mt-4 mt-sm-0"
                                            style={{ alignSelf: 'flex-end', marginBottom: '8px' }}
                                            aria-label={state.isLoading ? t('common.loading') : t('expert.welcomePage.recover')}
                                        >
                                            {state.isLoading ? t('common.loading') : t('expert.welcomePage.recover')}
                                        </BrButton>
                                    </div>
                                    {recoveryError && (
                                        <BrMessage status="danger" className="mt-3" message={recoveryError} />
                                    )}
                                </div>
                            )}
                        </div>
                    </BrCard>

                    {/* Divider */}
                    <div className="d-flex align-items-center gap-4 text-gray-20">
                        <div className="flex-fill border-top"></div>
                        <span className="text-base text-gray-60 text-weight-medium">{t('expert.welcomePage.fillProfile')}</span>
                        <div className="flex-fill border-top"></div>
                    </div>

                    {/* Card 2: Profile Form */}
                    <div ref={profileFormRef}>
                        <BrCard>
                            <div className="card-content">
                                {/* Header */}
                                <div className="text-center mb-5">
                                    <div className="d-inline-flex align-items-center justify-content-center bg-primary-default rounded-circle mb-3" style={{ width: '64px', height: '64px' }}>
                                        <i className="fas fa-clipboard-list text-3xl text-pure-0"></i>
                                    </div>
                                    <h1 className="text-3xl text-weight-bold text-gray-80 mb-2">
                                        {t('expert.welcomePage.expertProfile')}
                                    </h1>
                                    <p className="text-lg text-gray-60 m-0">
                                        {t('expert.welcomePage.fillProfileDesc')}
                                        <br />
                                        <span className="text-base">{t('expert.welcomePage.estimatedTime')}</span>
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="d-flex flex-column gap-6">
                                    {/* SEÇÃO 1: Experiência Profissional */}
                                    <div className="d-flex flex-column gap-4">
                                        <h2 className="text-base text-weight-bold text-gray-60 text-uppercase border-bottom border-gray-10 pb-2 mb-0">
                                            {t('expert.welcomePage.professionalExperience')}
                                        </h2>

                                        {/* Área de Atuação */}
                                        <BrSelect
                                            id="expertise"
                                            label={t('expert.welcomePage.mainExpertise')}
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            options={expertiseOptions as any}
                                            value={formData.expertise}
                                            onChange={(value) => handleFieldChange('expertise', value as ExpertProfile['expertise'])}
                                        />
                                        {errors.expertise && <span className="text-danger text-sm mt-1">! {errors.expertise}</span>}

                                        {/* Campo "Outra" expertise */}
                                        {formData.expertise === 'other' && (
                                            <div>
                                                <BrInput
                                                    id="other-expertise"
                                                    label={t('expert.welcomePage.specifyExpertise')}
                                                    value={formData.other_expertise || ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('other_expertise', e.target.value)}
                                                    placeholder={t('expert.welcomePage.specifyExpertisePlaceholder')}
                                                />
                                                {errors.other_expertise && <span className="text-danger text-sm mt-1">! {errors.other_expertise}</span>}
                                            </div>
                                        )}

                                        {/* Tempo de Atuação */}
                                        <BrSelect
                                            id="experience"
                                            label={t('expert.welcomePage.yearsInArea')}
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            options={experienceOptions as any}
                                            value={formData.years_experience}
                                            onChange={(value) => handleFieldChange('years_experience', value as ExpertProfile['years_experience'])}
                                        />
                                        {errors.years_experience && <span className="text-danger text-sm mt-1">! {errors.years_experience}</span>}

                                        {/* Escolaridade */}
                                        <BrSelect
                                            id="education"
                                            label={t('expert.welcomePage.educationLevel')}
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            options={educationOptions as any}
                                            value={formData.education}
                                            onChange={(value) => handleFieldChange('education', value as ExpertProfile['education'])}
                                        />
                                        {errors.education && <span className="text-danger text-sm mt-1">! {errors.education}</span>}
                                    </div>

                                    {/* SEÇÃO 2: Dados Demográficos */}
                                    <div className="d-flex flex-column gap-4">
                                        <h2 className="text-base text-weight-bold text-gray-60 text-uppercase border-bottom border-gray-10 pb-2 mb-0">
                                            {t('expert.welcomePage.demographics')}
                                        </h2>

                                        <div className="row">
                                            {/* Gênero */}
                                            <div className="col-12 col-md-6 mb-3 mb-md-0">
                                                <BrSelect
                                                    id="gender"
                                                    label={t('expert.welcomePage.genderLabel')}
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    options={genderOptions as any}
                                                    value={formData.gender}
                                                    onChange={(value) => handleFieldChange('gender', value as ExpertProfile['gender'])}
                                                />
                                                {errors.gender && <span className="text-danger text-sm mt-1">! {errors.gender}</span>}
                                            </div>

                                            {/* Faixa Etária */}
                                            <div className="col-12 col-md-6">
                                                <BrSelect
                                                    id="age"
                                                    label={t('expert.welcomePage.ageRangeLabel')}
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    options={ageOptions as any}
                                                    value={formData.age_range}
                                                    onChange={(value) => handleFieldChange('age_range', value as ExpertProfile['age_range'])}
                                                />
                                                {errors.age_range && <span className="text-danger text-sm mt-1">! {errors.age_range}</span>}
                                            </div>
                                        </div>

                                        {/* Necessidade de Acessibilidade */}
                                        <BrSelect
                                            id="accessibility"
                                            label={t('expert.welcomePage.accessibilityQuestion')}
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            options={accessibilityOptions as any}
                                            value={formData.accessibility_need}
                                            onChange={(value) => handleFieldChange('accessibility_need', value as ExpertProfile['accessibility_need'])}
                                        />
                                        {errors.accessibility_need && <span className="text-danger text-sm mt-1">! {errors.accessibility_need}</span>}

                                        {/* Campo "Outra" acessibilidade */}
                                        {formData.accessibility_need === 'other' && (
                                            <div>
                                                <BrInput
                                                    id="other-accessibility"
                                                    label={t('expert.welcomePage.specifyAccessibility')}
                                                    value={formData.other_accessibility || ''}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('other_accessibility', e.target.value)}
                                                    placeholder={t('expert.welcomePage.specifyAccessibilityPlaceholder')}
                                                />
                                                {errors.other_accessibility && <span className="text-danger text-sm mt-1">! {errors.other_accessibility}</span>}
                                            </div>
                                        )}

                                        {state.error && (
                                            <BrMessage status="danger" message={state.error} />
                                        )}
                                    </div>

                                    {/* Submit */}
                                    <BrButton
                                        primary
                                        block
                                        type="submit"
                                        disabled={state.isLoading}
                                        size="large"
                                        aria-label={state.isLoading ? t('common.loading') : t('expert.welcomePage.startEvaluation')}
                                    >
                                        {state.isLoading ? t('common.loading') : t('expert.welcomePage.startEvaluation') + ' →'}
                                    </BrButton>
                                </form>

                                {/* Privacy */}
                                <p className="text-center text-base text-gray-40 mt-4 m-0">
                                    {t('expert.welcomePage.privacyNote')}
                                </p>
                            </div>
                        </BrCard>
                    </div>
                </div>
            </div>
        </div>
    );
}
